import React from 'react';
import { Clock, Shield, CheckCircle2, ArrowRight, Phone, Wallet, HelpCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { licPlans } from '../lib/policiesData';
import { useTranslation } from 'react-i18next';
import SEOHead, { ADVISOR_NAME, ADVISOR_PHONE } from '../components/SEOHead';
import { trackWhatsAppClick } from '../services/analytics';

const PensionRetirementPlans = () => {
  const { t, i18n } = useTranslation();
  const isTe = i18n.language === 'te';
  const pensionPlans = licPlans.filter(p => p.category === 'retirement_planning');

  const pageSchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    'name': isTe ? 'ఎల్‌ఐసి పెన్షన్ & రిటైర్‌మెంట్ పాలసీలు | VSR Insurance Plus' : 'LIC Pension & Retirement Plans | Guaranteed Lifetime Income in India',
    'description': 'Explore sovereign-backed retirement plans with V. S. Reddy: LIC Jeevan Utsav (10% lifetime income), New Jeevan Shanti (guaranteed annuity), and Jeevan Umang.',
    'provider': {
      '@type': 'InsuranceAgency',
      'name': 'VSR Insurance Plus'
    }
  };

  return (
    <div className="min-h-screen pt-28 pb-20 bg-slate-50 dark:bg-slate-950">
      <SEOHead
        title={isTe ? "ఎల్‌ఐసి పెన్షన్ & రిటైర్‌మెంట్ పాలసీలు | 10% జీవితాంత ఆదాయం | VSR Insurance Plus" : "LIC Pension & Retirement Plans | Guaranteed Lifetime Income | VSR Insurance Plus"}
        description={isTe ? "ప్రభుత్వ హామీ గల పెన్షన్ పథకాలు: జీవన్ ఉత్సవ్ (10% స్థిర వార్షిక ఆదాయం), న్యూ జీవన్ శాంతి (సింగిల్ ప్రీమియం పెన్షన్), సరళ్ పెన్షన్ మరియు జీవన్ ఉమాంగ్. సీనియర్ అడ్వైజర్ వి. ఎస్. రెడ్డి." : "Plan a stress-free retirement with LIC pension policies: Jeevan Utsav (10% annual income), New Jeevan Shanti (locked annuity), and Jeevan Umang. Expert advisory by V. S. Reddy."}
        canonicalPath="/pension-retirement-plans"
        schemaJson={pageSchema}
        breadcrumbs={[{ name: isTe ? 'పెన్షన్ & రిటైర్‌మెంట్' : 'Pension & Retirement Plans', path: '/pension-retirement-plans' }]}
      />

      <div className="container mx-auto px-6 max-w-6xl">
        {/* Hero Section */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="inline-flex items-center gap-2 p-2.5 px-4 bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 rounded-full text-xs font-bold uppercase tracking-wider mb-4">
            <Wallet className="w-4 h-4" /> {isTe ? "ప్రభుత్వ హామీ గల రిటైర్‌మెంట్ ఆదాయం" : "Sovereign-Guaranteed Retirement Income"}
          </div>
          <h1 className="text-3xl md:text-5xl font-extrabold text-slate-900 dark:text-white mb-6 tracking-tight">
            {isTe ? "భారతదేశంలో ఎల్‌ఐసి పెన్షన్ & రిటైర్‌మెంట్ పథకాలు" : "LIC Pension & Retirement Plans in India"}
          </h1>
          <p className="text-base md:text-lg text-slate-600 dark:text-slate-400 leading-relaxed">
            {isTe
              ? "బ్యాంకు వడ్డీ రేట్లు తగ్గుతున్న మరియు ద్రవ్యోల్బణం పెరుగుతున్న నేటి కాలంలో, కేంద్ర ప్రభుత్వ సార్వభౌమ హామీతో జీవితాంతం 100 ఏళ్ల వరకు చట్టబద్ధమైన గ్యారెంటీడ్ పెన్షన్ పొందే సురక్షిత మార్గం."
              : "In an era where bank fixed deposit rates fluctuate and inflation chips away at purchasing power, LIC retirement plans lock in legally guaranteed lifetime incomes backed by the Government of India."}
          </p>
        </div>

        {/* 3 Pillars of Retirement Security */}
        <div className="grid md:grid-cols-3 gap-6 mb-16">
          <div className="bg-white dark:bg-slate-900 p-6 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm">
            <Clock className="w-10 h-10 text-emerald-500 mb-4" />
            <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-2">
              {isTe ? "జీవితాంతం స్థిరమైన పెన్షన్" : "Lifelong Predictability"}
            </h3>
            <p className="text-slate-500 text-sm leading-relaxed">
              {isTe
                ? "డబ్బు అయిపోతుందనే భయం వద్దు. 100 సంవత్సరాల వరకు ప్రతి సంవత్సరం క్రమం తప్పకుండా ఖాతాలో జమ అయ్యే గ్యారెంటీడ్ ఆదాయం."
                : "Never worry about running out of money. Receive guaranteed pensions or annual survival income every single year until age 100."}
            </p>
          </div>

          <div className="bg-white dark:bg-slate-900 p-6 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm">
            <Shield className="w-10 h-10 text-primary mb-4" />
            <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-2">
              {isTe ? "మార్కెట్ నష్టాల భయం సున్నా" : "Zero Market Volatility"}
            </h3>
            <p className="text-slate-500 text-sm leading-relaxed">
              {isTe
                ? "మార్కెట్ కుప్పకూలినా మీ పెన్షన్ మారదు. నాన్-లింక్డ్ ఎల్‌ఐసి పాలసీలు కాంట్రాక్టు ప్రకారం స్థిరమైన రాబడినిస్తాయి."
                : "Unlike mutual fund SWPs where market crashes can destroy capital, non-linked LIC policies maintain guaranteed contractual returns."}
            </p>
          </div>

          <div className="bg-white dark:bg-slate-900 p-6 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm">
            <Wallet className="w-10 h-10 text-sky-500 mb-4" />
            <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-2">
              {isTe ? "భాగస్వామికి ఉమ్మడి పెన్షన్" : "Joint Life Spouse Protection"}
            </h3>
            <p className="text-slate-500 text-sm leading-relaxed">
              {isTe
                ? "భార్యాభర్తలిద్దరూ బతికి ఉన్నంత వరకు పెన్షన్ కొనసాగుతుంది, ఆ తర్వాత పూర్తి కొనుగోలు ధర పిల్లలకు వాపసు ఇవ్వబడుతుంది."
                : "Pensions continue uninterrupted to your spouse, with 100% of purchase price returned to children upon demise."}
            </p>
          </div>
        </div>

        {/* Featured Pension Policies Grid */}
        <div className="mb-20">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <h2 className="text-2xl md:text-3xl font-extrabold text-slate-900 dark:text-white mb-3">
              {isTe ? "ప్రభుత్వ హామీ గల రిటైర్‌మెంట్ పాలసీలు" : "Sovereign-Backed Retirement Policies"}
            </h2>
            <p className="text-slate-500 text-sm">
              {isTe ? "మీ పదవీ విరమణ జీవితానికి సరిపోయే ఖచ్చితమైన పథకాన్ని ఎంచుకోండి." : "Compare guaranteed lifetime returns and choose the ideal pension timeline for your golden years."}
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {pensionPlans.map(plan => (
              <div key={plan.id} className="bg-white dark:bg-slate-900 rounded-3xl p-6 border border-slate-200 dark:border-slate-800 shadow-sm hover:shadow-xl transition-all duration-300 flex flex-col justify-between">
                <div>
                  <span className="inline-block px-3 py-1 bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400 rounded-full text-xs font-bold uppercase tracking-wider mb-4">
                    {isTe ? (plan.tagTe || plan.tag) : plan.tag}
                  </span>
                  <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-2">
                    {isTe ? (plan.nameTe || plan.name) : plan.name}
                  </h3>
                  <p className="text-slate-500 dark:text-slate-400 text-sm mb-6 leading-relaxed">
                    {isTe ? (plan.shortDescTe || plan.shortDesc) : plan.shortDesc}
                  </p>

                  <ul className="space-y-2.5 mb-6">
                    {(isTe && plan.featuresTe ? plan.featuresTe : plan.features).slice(0, 4).map((f, i) => (
                      <li key={i} className="flex gap-2 items-start text-xs text-slate-600 dark:text-slate-300">
                        <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="pt-4 border-t border-slate-100 dark:border-slate-800 flex items-center justify-between">
                  <div>
                    <span className="text-[11px] text-slate-400 block font-medium">{t('monthly_prem')}</span>
                    <span className="text-sm font-bold text-slate-900 dark:text-white">
                      {isTe ? (plan.premiumTe || plan.premium) : plan.premium}
                    </span>
                  </div>
                  <Link
                    to={`/plan/${plan.id}`}
                    className="px-4 py-2 bg-primary hover:bg-blue-600 text-white rounded-xl text-sm font-semibold transition-colors flex items-center gap-1.5"
                  >
                    {t('details')} <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Jeevan Utsav Spotlight */}
        <div className="bg-gradient-to-r from-emerald-900 to-teal-900 rounded-3xl p-8 md:p-12 text-white shadow-xl flex flex-col md:flex-row justify-between items-center gap-8 mb-16">
          <div className="max-w-xl">
            <span className="text-xs font-bold text-emerald-300 uppercase tracking-widest block mb-2">
              {isTe ? "ప్రత్యేక ప్రయోజనం" : "Special Spotlight"}
            </span>
            <h2 className="text-2xl md:text-3xl font-extrabold mb-3">
              {isTe ? "ఎల్‌ఐసి జీవన్ ఉత్సవ్ (ప్లాన్ 871): 10% జీవితాంత ఆదాయం" : "LIC Jeevan Utsav (Plan 871): 10% Guaranteed Income for Life"}
            </h2>
            <p className="text-emerald-100 text-sm leading-relaxed mb-4">
              {isTe
                ? "పరిమిత కాలం మాత్రమే ప్రీమియం చెల్లించండి (5 నుండి 16 సంవత్సరాలు). ఆ తర్వాత 100 ఏళ్ల వరకు ప్రతి సంవత్సరం బేసిక్ సమ్ అష్యూర్డ్‌పై 10% స్థిర ఆదాయం పొందండి."
                : "Pay limited premiums for just 5 to 16 years, then receive a guaranteed contractual 10% of Basic Sum Assured credited to your bank account every single year up to age 100."}
            </p>
            <Link
              to="/blog/lic-jeevan-utsav-explained"
              className="inline-flex items-center gap-2 text-yellow-300 font-bold hover:underline text-sm"
            >
              {isTe ? "జీవన్ ఉత్సవ్ పూర్తి గైడ్ చదవండి" : "Read In-depth Jeevan Utsav Guide"} <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="flex flex-col gap-3 w-full md:w-auto">
            <a
              href={`https://wa.me/919849664800?text=${encodeURIComponent(isTe ? 'నమస్కారం వి. ఎస్. రెడ్డి గారు, నేను జీవన్ ఉత్సవ్ 10% పెన్షన్ కొటేషన్ కోసం సంప్రదిస్తున్నాను.' : 'Hi V. S. Reddy, I am interested in calculating my retirement pension with LIC Jeevan Utsav. Please share an illustration.')}`}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackWhatsAppClick('pension_hub_whatsapp')}
              className="px-8 py-3.5 bg-yellow-400 hover:bg-yellow-300 text-slate-900 rounded-xl font-bold flex items-center justify-center gap-2 shadow-lg transition-transform hover:-translate-y-0.5"
            >
              <Phone className="w-4 h-4" /> {isTe ? "వాట్సాప్ కొటేషన్ పొందండి" : "WhatsApp Advisor for Quote"}
            </a>
            <Link
              to="/contact"
              className="px-8 py-3.5 bg-white/10 hover:bg-white/20 text-white rounded-xl font-medium text-center border border-white/20 transition-colors text-sm"
            >
              {isTe ? "ఉచిత కన్సల్టేషన్ బుక్ చేయండి" : "Book Free Consultation"}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PensionRetirementPlans;
